import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Delaunay } from 'd3-delaunay';
import ProposalGateOverlay from '../FrostGlassLoader/ProposalGateOverlay';
import ProposalApprovalForm from '../ProposalApproval/ProposalApprovalForm';
import TheaterLightbox, { ZoomHint } from '../TheaterLightbox/TheaterLightbox';
import { useContentful } from '../contentful-context';
import InternalBackLink from '../Internal/InternalBackLink';
import FallbackLogo from '../../assets/construx-logo.png';
import './BackyardProposal.css';

const IMG = '/backyard/proposal';

const PROPOSAL_ASSETS = [
  'hero.jpg',
  'living-area.jpg',
  'kitchen.jpg',
  'bathroom.jpg',
  'floor-plan.jpg',
  'front-elevation.jpg',
  'gallery-living-kitchen.jpg',
  'gallery-bedroom.jpg',
  'gallery-bath-vanity.jpg',
  'gallery-closet-wd.jpg',
  'gallery-exterior-entry.jpg',
  'gallery-backyard.jpg',
].map((file) => `${IMG}/${file}`);

const GALLERY_SLIDES = [
  { src: `${IMG}/gallery-living-kitchen.jpg`, alt: 'Living and kitchen area', caption: 'Living + kitchen', kicker: 'Finish Gallery · 01' },
  { src: `${IMG}/gallery-bedroom.jpg`, alt: 'Bedroom with window', caption: 'Bedroom', kicker: 'Finish Gallery · 02' },
  { src: `${IMG}/gallery-bath-vanity.jpg`, alt: 'Bathroom vanity', caption: 'Bath vanity', kicker: 'Finish Gallery · 03' },
  { src: `${IMG}/gallery-closet-wd.jpg`, alt: 'Room with closet doors', caption: 'Closet / W-D', kicker: 'Finish Gallery · 04' },
  { src: `${IMG}/gallery-exterior-entry.jpg`, alt: 'Exterior siding and entry', caption: 'Exterior entry', kicker: 'Finish Gallery · 05' },
  { src: `${IMG}/gallery-backyard.jpg`, alt: 'Backyard exterior wide view', caption: 'Backyard siting', kicker: 'Finish Gallery · 06' },
];

const VISUAL_SLIDES = [
  { src: `${IMG}/living-area.jpg`, alt: 'Open, bright modern ADU living area with vaulted ceiling', caption: 'Open, bright and modern', kicker: 'Visual Standard · Living Area' },
  { src: `${IMG}/kitchen.jpg`, alt: 'Compact modern ADU kitchen with stainless appliances', caption: 'Compact + modern', kicker: 'Visual Standard · Kitchen' },
  { src: `${IMG}/bathroom.jpg`, alt: 'Full bathroom with glass shower and modern fixtures', caption: 'Full finish-out', kicker: 'Visual Standard · Bathroom' },
];

const PLAN_SLIDES = [
  { src: `${IMG}/floor-plan.jpg`, alt: 'ADU floor plan drawing with dimensions', caption: 'Plan layout', kicker: 'DWG-01 / Floor Plan' },
  { src: `${IMG}/front-elevation.jpg`, alt: 'ADU front elevation drawing', caption: 'Exterior elevation', kicker: 'DWG-02 / Front Elevation' },
];

const GALLERY_LAYOUT = ['ga', 'gb', 'gc', 'gd', 'ge', 'gf'];

const NAV_LINKS = [
  { id: 'summary', label: 'Summary' },
  { id: 'scope', label: 'Scope' },
  { id: 'investment', label: 'Investment' },
  { id: 'plan', label: 'Plan' },
  { id: 'gallery', label: 'Gallery' },
  { id: 'process', label: 'Process' },
];

const INVESTMENT_ROWS = [
  {
    title: 'Planning, permitting & project coordination',
    desc: 'Permit coordination, inspections, project management, builder supervision and document control.',
    amount: '$10,000',
  },
  {
    title: 'Site preparation, foundation & utility coordination',
    desc: 'Layout, site prep, slab / foundation allowance, basic trench coordination and utility connection support.',
    amount: '$30,000',
  },
  {
    title: 'Construx R1AS structural shell',
    desc: 'Delivered $28,000 framing / sheathing system, assembly labor, fastening, lift / equipment allowance.',
    amount: '$38,000',
  },
  {
    title: 'Roofing + exterior envelope',
    desc: 'Roofing, Hardie / lap siding, soffit, fascia, gutters, exterior trim, caulking and paint.',
    amount: '$33,000',
  },
  {
    title: 'Windows + exterior entry door',
    desc: 'Window and exterior door package based on plan openings and modern glazed entry-door standard.',
    amount: '$10,000',
  },
  {
    title: 'Electrical, plumbing & HVAC systems',
    desc: 'Electrical and plumbing rough / finish, kitchen / bath / W-D connections, devices, smoke detectors and mini-split HVAC.',
    amount: '$41,000',
  },
  {
    title: 'Insulation, drywall & paint',
    desc: 'Insulation, sheetrock, tape, float, texture, interior paint and ceiling finish work.',
    amount: '$17,000',
  },
  {
    title: 'Flooring, trim, interior doors & carpentry',
    desc: 'LVP / wood-look flooring allowance, baseboards, casing, interior doors, closet and finish carpentry.',
    amount: '$13,000',
  },
  {
    title: 'Kitchen finish package',
    desc: 'Cabinetry, countertop, backsplash, sink / faucet and basic stainless appliance allowance.',
    amount: '$19,000',
  },
  {
    title: 'Full bathroom finish package',
    desc: 'Waterproofing, tile / shower, vanity, toilet, mirror, lighting and fixture allowance.',
    amount: '$15,000',
  },
  {
    title: 'Final punch, cleaning & warranty support',
    desc: 'Final inspections, punch list, cleaning, client handoff and warranty support.',
    amount: '$9,000',
  },
];

const SCOPES = [
  {
    n: '01',
    title: 'Foundation + Structural Shell',
    items: [
      'Site layout and foundation allowance',
      'Construx R1AS framing / sheathing system ($28,000 delivered package)',
      'Shell assembly labor and fastening',
      'Lift / equipment allowance',
    ],
  },
  {
    n: '02',
    title: 'Exterior Envelope',
    items: [
      'Roofing system',
      'Hardie / lap siding and exterior trim',
      'Soffit, fascia and gutters',
      'Exterior caulking and paint',
    ],
  },
  {
    n: '03',
    title: 'Mechanical / Electrical / Plumbing',
    items: [
      'Electrical rough-in and finish',
      'Plumbing rough-in and finish',
      'Smoke detectors, switches, outlets',
      'Mini-split HVAC system',
    ],
  },
  {
    n: '04',
    title: 'Interior Finish',
    items: [
      'Insulation',
      'Sheetrock, tape, float, texture and paint',
      'Flooring and baseboards',
      'Interior doors, casing, closet and finish carpentry',
    ],
  },
  {
    n: '05',
    title: 'Kitchen Package',
    items: [
      'Compact ADU kitchen cabinetry',
      'Countertop and backsplash allowance',
      'Sink and faucet',
      'Basic stainless appliance allowance',
    ],
  },
  {
    n: '06',
    title: 'Bathroom Package',
    items: [
      'Full bathroom finish-out',
      'Waterproofing and tile / shower allowance',
      'Vanity, toilet, mirror and lighting',
      'Fixture / accessory allowance',
    ],
  },
];

const TIMELINE = [
  { n: '1', title: 'Site Review', desc: 'Confirm access, utilities, location, setbacks, selections and permit path.' },
  { n: '2', title: 'Permits', desc: 'Finalize documents, submit, coordinate comments and inspections.' },
  { n: '3', title: 'Foundation + Shell', desc: 'Prepare site, pour foundation and assemble the Construx R1AS shell.' },
  { n: '4', title: 'MEP + Finish', desc: 'Complete rough-ins, insulation, drywall, finishes, kitchen and bathroom.' },
  { n: '5', title: 'Final Turnover', desc: 'Final inspections, punch list, cleaning and client handoff.' },
];

const ArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const BrandMark = ({ stroke = '#BE4128' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="3" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

function useMeshCanvas(canvasRef, opts = {}) {
  const optsRef = useRef(opts);
  useEffect(() => { optsRef.current = opts; });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let W = 0, H = 0, pts = [], vx = [], vy = [], n = 0;
    let edges = [], trav = [], raf = 0;

    function build() {
      const { density = 6.5, maxLen = 230 } = optsRef.current;
      const r = canvas.parentElement.getBoundingClientRect();
      W = r.width; H = r.height;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      canvas.style.width = W + 'px';
      canvas.style.height = H + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const step = Math.max(120, Math.min(180, Math.sqrt(W * H) / density));
      pts = []; vx = []; vy = [];
      for (let y = -step * 0.5; y < H + step; y += step) {
        for (let x = -step * 0.5; x < W + step; x += step) {
          pts.push([
            x + (Math.random() - 0.5) * step * 0.6,
            y + (Math.random() - 0.5) * step * 0.6,
          ]);
          const a = Math.random() * 6.28, sp = 0.1 + Math.random() * 0.16;
          vx.push(Math.cos(a) * sp);
          vy.push(Math.sin(a) * sp);
        }
      }
      n = pts.length;
      trav = [];
      for (let t = 0; t < Math.max(5, Math.round(n / 20)); t++) {
        trav.push({ idx: 0, p: Math.random(), sp: 0.004 + Math.random() * 0.006 });
      }
      void maxLen;
    }

    function frame(drift) {
      const { maxLen = 230 } = optsRef.current;
      if (drift) {
        for (let i = 0; i < n; i++) {
          pts[i][0] += vx[i]; pts[i][1] += vy[i];
          if (pts[i][0] < -70 || pts[i][0] > W + 70) vx[i] *= -1;
          if (pts[i][1] < -70 || pts[i][1] > H + 70) vy[i] *= -1;
        }
      }
      const flat = new Float64Array(n * 2);
      for (let k = 0; k < n; k++) { flat[k * 2] = pts[k][0]; flat[k * 2 + 1] = pts[k][1]; }

      const del = new Delaunay(flat);
      const tri = del.triangles, half = del.halfedges;
      ctx.clearRect(0, 0, W, H);
      edges = [];

      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(210,119,60,0.2)';
      ctx.beginPath();
      for (let e = 0; e < tri.length; e++) {
        const o = half[e];
        if (e > o) {
          const p = tri[e], q = tri[(e % 3 === 2) ? e - 2 : e + 1];
          const dx = pts[p][0] - pts[q][0], dy = pts[p][1] - pts[q][1];
          if (dx * dx + dy * dy < maxLen * maxLen) {
            ctx.moveTo(pts[p][0], pts[p][1]);
            ctx.lineTo(pts[q][0], pts[q][1]);
            edges.push([p, q]);
          }
        }
      }
      ctx.stroke();

      ctx.fillStyle = 'rgba(229,166,113,0.55)';
      for (let v = 0; v < n; v++) {
        ctx.beginPath(); ctx.arc(pts[v][0], pts[v][1], 1.4, 0, 6.28); ctx.fill();
      }

      if (edges.length) {
        ctx.save();
        ctx.shadowBlur = 8;
        ctx.shadowColor = 'rgba(210,119,60,0.7)';
        for (let ti = 0; ti < trav.length; ti++) {
          const tr = trav[ti];
          if (!edges[tr.idx]) { tr.idx = (Math.random() * edges.length) | 0; tr.p = 0; }
          const ed = edges[tr.idx], A = pts[ed[0]], B = pts[ed[1]];
          const px = A[0] + (B[0] - A[0]) * tr.p;
          const py = A[1] + (B[1] - A[1]) * tr.p;
          ctx.fillStyle = 'rgba(229,166,113,0.7)';
          ctx.beginPath(); ctx.arc(px, py, 2, 0, 6.28); ctx.fill();
          if (drift) {
            tr.p += tr.sp;
            if (tr.p >= 1) { tr.idx = (Math.random() * edges.length) | 0; tr.p = 0; }
          }
        }
        ctx.restore();
      }
    }

    function loop() { frame(true); raf = requestAnimationFrame(loop); }

    build();
    if (reduce) { frame(false); } else { loop(); }

    function handleResize() {
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
      build();
      if (reduce) { frame(false); } else { loop(); }
    }
    function handleVisibility() {
      if (document.hidden) {
        if (raf) { cancelAnimationFrame(raf); raf = 0; }
      } else if (!reduce && !raf) {
        loop();
      }
    }

    window.addEventListener('resize', handleResize);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [canvasRef]);
}

function useReveal(pageRef) {
  useEffect(() => {
    const page = pageRef.current;
    if (!page) return;
    const els = Array.from(page.querySelectorAll('.reveal'));

    if (!('IntersectionObserver' in window)) {
      els.forEach(e => e.classList.add('in'));
      return;
    }

    const io = new IntersectionObserver(
      entries => entries.forEach(x => {
        if (x.isIntersecting) { x.target.classList.add('in'); io.unobserve(x.target); }
      }),
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );
    els.forEach(e => io.observe(e));

    const safety = setTimeout(() => els.forEach(e => e.classList.add('in')), 2500);
    return () => { io.disconnect(); clearTimeout(safety); };
  }, [pageRef]);
}

function useNavState(navRef, heroRef) {
  useEffect(() => {
    const nav = navRef.current;
    const hero = heroRef.current;
    if (!nav || !hero) return;

    function update() {
      const solid = window.scrollY > 40;
      nav.classList.toggle('solid', solid);
      const h = hero.getBoundingClientRect();
      nav.classList.toggle('ondark', h.bottom > 78 && !solid);
    }

    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, [navRef, heroRef]);
}

function useConstruxLogo(id, fallback = FallbackLogo) {
  const content = useContentful();
  const entry = content.images.find(img => img.id === id);
  return entry ? entry.image.fields.file.url : fallback;
}

export default function BackyardProposal() {
  const pageRef  = useRef(null);
  const navRef   = useRef(null);
  const heroRef  = useRef(null);
  const logoUrl  = useConstruxLogo('Construx-Black-FullColor-Tag');
  const whiteLogoUrl = useConstruxLogo('Construx-White-FullColor-Tag', logoUrl);
  const meshRef  = useRef(null);
  const [lightbox, setLightbox] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const openLightbox = useCallback((slides, index) => {
    setLightbox({ slides, index });
  }, []);

  const closeLightbox = useCallback(() => {
    setLightbox(null);
  }, []);

  useMeshCanvas(meshRef, { density: 6.5 });
  useReveal(pageRef);
  useNavState(navRef, heroRef);

  useEffect(() => {
    const prev = document.documentElement.style.scrollPaddingTop;
    document.documentElement.style.scrollPaddingTop = '78px';
    return () => { document.documentElement.style.scrollPaddingTop = prev; };
  }, []);

  const scrollTo = useCallback((id) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    const onScroll = () => setMenuOpen(false);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [menuOpen]);

  return (
    <div className="backyard-proposal-page" ref={pageRef}>
      <ProposalGateOverlay assetUrls={PROPOSAL_ASSETS} />
      {lightbox ? (
        <TheaterLightbox
          slides={lightbox.slides}
          index={lightbox.index}
          onClose={closeLightbox}
          onChange={(index) => setLightbox(prev => ({ ...prev, index }))}
        />
      ) : null}
      <Helmet>
        <title>Construx Backyard — Detached ADU Construction Proposal</title>
        <meta name="description" content="A complete, finished 550 SF backyard ADU built on the Construx R1AS framing system — client proposal by Austin Remodel Pro." />
      </Helmet>

      <nav
        className={`nav ondark${menuOpen ? ' menu-open' : ''}`}
        id="nav"
        ref={navRef}
        aria-label="Proposal navigation"
      >
        <div className="wrap row">
          <a className="brand" href="#top" onClick={e => { e.preventDefault(); scrollTo('top'); }}>
            <img src={logoUrl} alt="Construx" className="brand-logo brand-logo-dark" />
            <img src={whiteLogoUrl} alt="" className="brand-logo brand-logo-light" aria-hidden="true" />
          </a>
          <div className="navlinks">
            {NAV_LINKS.map(({ id, label }) => (
              <a key={id} href={`#${id}`} onClick={e => { e.preventDefault(); scrollTo(id); }}>
                {label}
              </a>
            ))}
          </div>
          <div className="right">
            <InternalBackLink className="nav-internal-back" />
            <a
              className="btn clay nav-approve-desktop"
              href="#acceptance"
              onClick={e => { e.preventDefault(); scrollTo('acceptance'); }}
            >
              Approve proposal
            </a>
            <button
              type="button"
              className="nav-menu-btn"
              aria-expanded={menuOpen}
              aria-controls="nav-drawer"
              onClick={() => setMenuOpen(open => !open)}
            >
              <span className="nav-menu-btn__bars" aria-hidden="true">
                <i /><i /><i />
              </span>
              <span className="sr-only">{menuOpen ? 'Close menu' : 'Open menu'}</span>
            </button>
          </div>
        </div>
        <div className="nav-drawer" id="nav-drawer" hidden={!menuOpen}>
          <div className="wrap nav-drawer__inner">
            {NAV_LINKS.map(({ id, label }) => (
              <a key={id} href={`#${id}`} onClick={e => { e.preventDefault(); scrollTo(id); }}>
                {label}
              </a>
            ))}
            <InternalBackLink className="nav-internal-back nav-internal-back--drawer" />
            <a
              className="btn clay nav-approve-mobile"
              href="#acceptance"
              onClick={e => { e.preventDefault(); scrollTo('acceptance'); }}
            >
              Approve proposal
            </a>
          </div>
        </div>
      </nav>

      <header className="hero" id="top" ref={heroRef}>
        <div className="ph" style={{ backgroundImage: `url('${IMG}/hero.jpg')` }} aria-hidden="true" />
        <div className="scrim" aria-hidden="true" />
        <div className="inner">
          <div className="wrap">
            <div className="lbl line reveal" style={{ color: 'var(--ember-soft)' }}>
              Client Proposal · Finished ADU Package
            </div>
            <h1 className="reveal" style={{ marginTop: 18 }}>
              Detached ADU <span className="grad">Construction Proposal.</span>
            </h1>
            <p className="lead reveal">
              A complete, finished 550 SF backyard ADU built on the Construx R1AS framing system — a clean, modern, rental-ready one-bedroom unit with kitchen, full bathroom, living area, and W/D.
            </p>
            <div className="specrow reveal">
              <div className="c">
                <div className="k">Proposal model</div>
                <div className="v accent">$235,000</div>
              </div>
              <div className="c">
                <div className="k">ADU size</div>
                <div className="v">550 SF</div>
              </div>
              <div className="c">
                <div className="k">Price basis</div>
                <div className="v">
                  $427<span style={{ fontSize: '.5em', letterSpacing: 0 }}>/SF</span>
                </div>
              </div>
              <div className="c">
                <div className="k">Configuration</div>
                <div className="v">1 Bed · 1 Bath</div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <section className="sec" id="summary">
        <div className="wrap">
          <div className="hd">
            <div className="top">
              <div className="lbl line reveal">01 — Proposal Summary</div>
              <div className="no reveal">REF / SUM-01</div>
            </div>
            <h2 className="reveal">A complete ADU — <span className="grad">within weeks.</span></h2>
            <p className="reveal">
              This proposal is written for a client who wants a complete, move-in-ready backyard unit. It includes the major construction scopes needed to deliver a finished, rental-ready ADU.
            </p>
          </div>
          <div className="sgrid">
            <div className="scard reveal">
              <div className="k">Unit type</div>
              <div className="v">1 Bed / 1 Bath</div>
              <p>Living room, kitchen, W/D area and full bathroom.</p>
            </div>
            <div className="scard reveal">
              <div className="k">Framing system</div>
              <div className="v">Construx R1AS</div>
              <p>Structural framing and sheathing system basis.</p>
            </div>
            <div className="scard reveal">
              <div className="k">Exterior</div>
              <div className="v">Hardie + Paint</div>
              <p>Painted lap-siding look with a modern entry condition.</p>
            </div>
            <div className="scard reveal">
              <div className="k">Finish level</div>
              <div className="v">Modern Rental</div>
              <p>Clean, bright, durable, client-ready ADU standard.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="sec dark" id="visual">
        <canvas ref={meshRef} className="meshbg" aria-hidden="true" />
        <div className="wrap">
          <div className="hd">
            <div className="top">
              <div className="lbl line reveal">02 — Visual Standard</div>
              <div className="no reveal">REF / FIN-02</div>
            </div>
            <h2 className="reveal">The finish direction this proposal is built around.</h2>
            <p className="reveal">
              These reference photos define the look and finish level carried in the proposal: light flooring, white interior, modern kitchen, full bathroom, clean exterior, and vaulted ceilings.
            </p>
          </div>
          <div className="pgrid reveal">
            <button
              type="button"
              className="pmain is-zoomable"
              onClick={() => openLightbox(VISUAL_SLIDES, 0)}
              aria-label="Open living area photo in fullscreen"
            >
              <img src={`${IMG}/living-area.jpg`} alt="Open, bright modern ADU living area with vaulted ceiling" />
              <ZoomHint />
              <div className="cap">
                <div className="k">Living Area</div>
                <b>Open, bright and modern</b>
                <p>Large windows, clean white walls, modern entry door, light wood-look flooring.</p>
              </div>
            </button>
            <div className="pstack">
              <button
                type="button"
                className="pcard is-zoomable"
                onClick={() => openLightbox(VISUAL_SLIDES, 1)}
                aria-label="Open kitchen photo in fullscreen"
              >
                <img src={`${IMG}/kitchen.jpg`} alt="Compact modern ADU kitchen with stainless appliances" />
                <ZoomHint />
                <div className="cap">
                  <div className="k">Kitchen</div>
                  <b>Compact + modern</b>
                </div>
              </button>
              <button
                type="button"
                className="pcard is-zoomable"
                onClick={() => openLightbox(VISUAL_SLIDES, 2)}
                aria-label="Open bathroom photo in fullscreen"
              >
                <img src={`${IMG}/bathroom.jpg`} alt="Full bathroom with glass shower and modern fixtures" />
                <ZoomHint />
                <div className="cap">
                  <div className="k">Bathroom</div>
                  <b>Full finish-out</b>
                </div>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="sec bone" id="scope">
        <div className="wrap">
          <div className="hd">
            <div className="top">
              <div className="lbl line reveal">03 — Included Scope</div>
              <div className="no reveal">REF / SCP-06</div>
            </div>
            <h2 className="reveal">What the proposal includes.</h2>
            <p className="reveal">Written in plain client language — the finished ADU package, scope by scope.</p>
          </div>
          <div className="scopes">
            {SCOPES.map(scope => (
              <div className="scope reveal" key={scope.n}>
                <div className="sh">
                  <span className="sn">{scope.n}</span>
                  <h3>{scope.title}</h3>
                </div>
                <ul>
                  {scope.items.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="sec dark" id="investment">
        <div className="wrap">
          <div className="hd">
            <div className="top">
              <div className="lbl line reveal">04 — Investment Model</div>
              <div className="no reveal">REF / INV-01</div>
            </div>
            <h2 className="reveal">Client proposal amount.</h2>
            <p className="reveal">A client-facing budget allocation for the finished ADU package — not an internal cost sheet.</p>
          </div>
          <div className="invest">
            <div className="total reveal">
              <div className="k">Total proposal model</div>
              <div className="big">$235,000</div>
              <p>
                For a complete 550 SF finished ADU based on the plan layout, the Construx R1AS structural system, and the photo finish standard shown in this proposal.
              </p>
              <div className="tnote">
                <b>Presentation language</b>
                Includes construction, finish work, project coordination and a built-in allowance structure. Final fixed pricing is confirmed after site verification and selections.
              </div>
            </div>
            <div className="ptable reveal">
              {INVESTMENT_ROWS.map(row => (
                <div className="prow" key={row.title}>
                  <div>
                    <h3>{row.title}</h3>
                    <p>{row.desc}</p>
                  </div>
                  <div className="num">{row.amount}</div>
                </div>
              ))}
              <div className="prow sum">
                <h3>Total proposal model</h3>
                <div className="num">$235,000</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="sec" id="plan">
        <div className="wrap">
          <div className="hd">
            <div className="top">
              <div className="lbl line reveal">05 — Plan Basis</div>
              <div className="no reveal">DWG / PLN-01</div>
            </div>
            <h2 className="reveal">The plan layout this model is based on.</h2>
            <p className="reveal">
              A compact one-bedroom ADU with living room, kitchen, full bathroom, W/D area, closet, window callouts and exterior elevations.
            </p>
          </div>
          <div className="plan reveal">
            <div className="plancard">
              <button
                type="button"
                className="img is-zoomable"
                onClick={() => openLightbox(PLAN_SLIDES, 0)}
                aria-label="Open floor plan drawing in fullscreen"
              >
                <span className="code">DWG-01 / FLOOR PLAN</span>
                <img src={`${IMG}/floor-plan.jpg`} alt="ADU floor plan drawing with dimensions" />
              </button>
              <div className="tx">
                <h3>Plan layout</h3>
                <p>Model basis: approximately 550 SF ADU with living room, bedroom, kitchen, bathroom, W/D area and closet.</p>
              </div>
            </div>
            <div className="plancard">
              <button
                type="button"
                className="img is-zoomable"
                onClick={() => openLightbox(PLAN_SLIDES, 1)}
                aria-label="Open front elevation drawing in fullscreen"
              >
                <span className="code">DWG-02 / FRONT ELEVATION</span>
                <img src={`${IMG}/front-elevation.jpg`} alt="ADU front elevation drawing" />
              </button>
              <div className="tx">
                <h3>Exterior elevation</h3>
                <p>Clean painted-siding look with a simple modern entry condition and standard ADU massing.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="sec bone" id="gallery">
        <div className="wrap">
          <div className="hd">
            <div className="top">
              <div className="lbl line reveal">06 — Finish Gallery</div>
              <div className="no reveal">DOC / FIN-06</div>
            </div>
            <h2 className="reveal">Reference finish photos.</h2>
            <p className="reveal">The style and finish quality the proposal is modeled around (these are not AI — they&apos;re real project photos).</p>
          </div>
          <div className="ggrid reveal">
            {GALLERY_SLIDES.map((slide, i) => (
              <button
                key={slide.src}
                type="button"
                className={`${GALLERY_LAYOUT[i]} is-zoomable`}
                onClick={() => openLightbox(GALLERY_SLIDES, i)}
                aria-label={`Open ${slide.caption} in fullscreen`}
              >
                <img src={slide.src} alt={slide.alt} />
                <ZoomHint />
                <figcaption>{slide.caption}</figcaption>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="sec dark" id="process">
        <div className="wrap">
          <div className="hd">
            <div className="top">
              <div className="lbl line reveal">07 — Process</div>
              <div className="no reveal">SCH / PHS-05</div>
            </div>
            <h2 className="reveal">How the project moves from approval to completion.</h2>
          </div>
          <div className="timeline reveal">
            {TIMELINE.map(step => (
              <div className="tstep" key={step.n}>
                <div className="n">{step.n}</div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="sec" id="clarifications">
        <div className="wrap">
          <div className="hd">
            <div className="top">
              <div className="lbl line reveal">08 — Clarifications</div>
              <div className="no reveal">REF / CLR-01</div>
            </div>
            <h2 className="reveal">Assumptions and exclusions.</h2>
            <p className="reveal">Items to confirm before converting this proposal model into a final fixed contract.</p>
          </div>
          <div className="twocol reveal">
            <div className="cbox">
              <h3><span className="dot" />Assumptions</h3>
              <ul>
                <li>Pricing basis is a 550 SF detached ADU.</li>
                <li>Finished as a one-bedroom / one-bath unit with kitchen and W/D area.</li>
                <li>Standard site access and normal foundation conditions.</li>
                <li>Standard utility connection assumptions pending field verification.</li>
                <li>Finish standard follows the photos shown in this proposal.</li>
              </ul>
            </div>
            <div className="cbox excl">
              <h3><span className="dot" />Not included unless added</h3>
              <ul>
                <li>Major utility upgrades, tap fees or long trenching beyond allowance.</li>
                <li>Main panel upgrade or utility-company work.</li>
                <li>Survey, engineering revisions or special city requirements beyond allowance.</li>
                <li>Landscaping, fencing, decks, patios, driveway or exterior site upgrades.</li>
                <li>Premium appliances, custom cabinetry, upgraded tile or luxury finishes above allowance.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="sec bone" id="acceptance">
        <div className="wrap">
          <div className="hd">
            <div className="top">
              <div className="lbl line reveal">09 — Acceptance</div>
              <div className="no reveal">DOC / APR-01</div>
            </div>
            <h2 className="reveal">Proposal approval.</h2>
          </div>
          <div className="reveal">
            <ProposalApprovalForm />
          </div>
          <div className="vnote reveal">
            <b>Client-facing note —</b> This is a proposal model for a finished ADU package. Final fixed pricing depends on site conditions, utility distances, permitting requirements, and final selections confirmed during site verification.
          </div>
        </div>
      </section>

      <footer>
        <div className="wrap">
          <div className="top">
            <a className="brand" href="#top" onClick={e => { e.preventDefault(); scrollTo('top'); }}>
              <span className="mk"><BrandMark stroke="#17130A" /></span>
              <span>
                <span className="nm">constru<i>X</i></span>
                <span className="sub" style={{ color: 'rgba(255,255,255,.5)' }}>Backyard System</span>
              </span>
            </a>
            <div className="meta">
              Client ADU Proposal Model<br />
              Construx R1AS · Finished ADU · 550 SF<br />
              Prepared by Austin Remodel Pro
            </div>
          </div>
          <div className="legal">
            This document is a client-facing proposal model. Final fixed pricing depends on site conditions, utility distances, permitting requirements and final selections confirmed during site verification. Allowances apply.
          </div>
        </div>
      </footer>
    </div>
  );
}
